import React, { useState } from 'react';
import { ArrowLeft, ArrowRight, Car, Euro, Calendar, Mail, CreditCard } from 'lucide-react';
import { useToast } from '../../contexts/ToastContext';

interface Studio {
  _id: string;
  ragioneSociale: string;
  citta: string;
  provincia: string;
}

interface DatiAtto {
  targa: string;
  prezzoVendita: number;
  soggettoIVA: boolean;
  tipoAtto: 'art56' | 'normale';
  dataAppuntamento: string;
  oraAppuntamento: string;
  modalitaPagamento: 'venditore' | 'bonifico';
  emailVenditore: string;
}

interface StepDatiAttoProps {
  studiSelezionati: Studio[];
  onComplete: (dati: DatiAtto) => void;
  onBack: () => void;
  initialData: DatiAtto | null;
}

const StepDatiAtto: React.FC<StepDatiAttoProps> = ({ studiSelezionati, onComplete, onBack, initialData }) => {
  const [formData, setFormData] = useState<DatiAtto>(
    initialData || {
      targa: '',
      prezzoVendita: 0,
      soggettoIVA: false,
      tipoAtto: 'normale',
      dataAppuntamento: '',
      oraAppuntamento: '',
      modalitaPagamento: 'venditore',
      emailVenditore: ''
    }
  );

  const toast = useToast();
  const handleChange = (field: keyof DatiAtto, value: any) => {
    setFormData({ ...formData, [field]: value });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    // Validazioni
    if (!formData.targa.trim()) {
      toast.error('Inserisci la targa del veicolo');
      return;
    }

    if (formData.prezzoVendita <= 0) {
      toast.error('Inserisci un prezzo di vendita valido');
      return;
    }

    if (!formData.dataAppuntamento) {
      toast.error('Seleziona la data dell\'appuntamento');
      return;
    }

    if (!formData.oraAppuntamento) {
      toast.error('Seleziona l\'ora dell\'appuntamento');
      return;
    }

    if (!formData.emailVenditore.trim() || !formData.emailVenditore.includes('@')) {
      toast.error('Inserisci un\'email valida del venditore');
      return;
    }

    onComplete(formData);
  };

  // Data minima: oggi
  const getMinDate = () => {
    return new Date().toISOString().split('T')[0];
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
          Dati dell'Atto di Vendita
        </h2>
        <p className="text-gray-600 dark:text-gray-400">
          Compila i dettagli dell'atto per {studiSelezionati.length} {studiSelezionati.length === 1 ? 'studio selezionato' : 'studi selezionati'}
        </p>
      </div>

      {/* Studi Selezionati */}
      <div className="bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 rounded-lg p-4">
        <h3 className="text-sm font-semibold text-purple-900 dark:text-purple-100 mb-2">
          Studi che riceveranno la richiesta:
        </h3>
        <div className="space-y-1">
          {studiSelezionati.map((studio) => (
            <div key={studio._id} className="text-sm text-purple-800 dark:text-purple-200">
              • {studio.ragioneSociale} - {studio.citta} ({studio.provincia})
            </div>
          ))}
        </div>
      </div>

      {/* Dati Veicolo */}
      <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-6 space-y-4">
        <h3 className="text-base font-semibold text-gray-900 dark:text-white flex items-center gap-2">
          <Car className="w-5 h-5" />
          Dati Veicolo
        </h3>

        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Targa *
            </label>
            <input
              type="text"
              value={formData.targa}
              onChange={(e) => handleChange('targa', e.target.value.toUpperCase())}
              placeholder="ES: AB123CD"
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Prezzo di Vendita (€) *
            </label>
            <div className="relative">
              <Euro className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="number"
                value={formData.prezzoVendita || ''}
                onChange={(e) => handleChange('prezzoVendita', parseFloat(e.target.value) || 0)}
                placeholder="0.00"
                min="0"
                step="0.01"
                className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                required
              />
            </div>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <input
            type="checkbox"
            id="soggettoIVA"
            checked={formData.soggettoIVA}
            onChange={(e) => handleChange('soggettoIVA', e.target.checked)}
            className="w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
          />
          <label htmlFor="soggettoIVA" className="text-sm font-medium text-gray-700 dark:text-gray-300">
            Soggetto IVA
          </label>
        </div>
      </div>

      {/* Tipo Atto */}
      <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-6">
        <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-4">
          Tipo di Atto *
        </h3>
        <div className="grid md:grid-cols-2 gap-3">
          {[
            { value: 'art56', label: 'Trasferimento - Art. 56', description: 'ex art. 56 del D. LGS. 15/12/1997 N. 446' },
            { value: 'normale', label: 'Trasferimento di proprietà', description: 'trasferimento normale (no Dini)' }
          ].map((tipo) => (
            <label
              key={tipo.value}
              className={`flex flex-col p-4 border-2 rounded-lg cursor-pointer transition-all ${
                formData.tipoAtto === tipo.value
                  ? 'border-purple-500 bg-purple-50 dark:bg-purple-900/20'
                  : 'border-gray-200 dark:border-gray-700 hover:border-purple-300'
              }`}
            >
              <input
                type="radio"
                name="tipoAtto"
                value={tipo.value}
                checked={formData.tipoAtto === tipo.value}
                onChange={(e) => handleChange('tipoAtto', e.target.value as any)}
                className="sr-only"
              />
              <span className="font-semibold text-gray-900 dark:text-white">{tipo.label}</span>
              <span className="text-xs text-gray-600 dark:text-gray-400 mt-1">{tipo.description}</span>
            </label>
          ))}
        </div>
      </div>

      {/* Appuntamento */}
      <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-6 space-y-4">
        <h3 className="text-base font-semibold text-gray-900 dark:text-white flex items-center gap-2">
          <Calendar className="w-5 h-5" />
          Data e Ora Appuntamento
        </h3>

        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Data *
            </label>
            <input
              type="date"
              value={formData.dataAppuntamento}
              onChange={(e) => handleChange('dataAppuntamento', e.target.value)}
              min={getMinDate()}
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Ora *
            </label>
            <input
              type="time"
              value={formData.oraAppuntamento}
              onChange={(e) => handleChange('oraAppuntamento', e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
              required
            />
          </div>
        </div>
      </div>

      {/* Modalità Pagamento */}
      <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-6">
        <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
          <CreditCard className="w-5 h-5" />
          Modalità di Pagamento *
        </h3>
        <div className="grid md:grid-cols-2 gap-3">
          <label
            className={`flex items-start p-4 border-2 rounded-lg cursor-pointer transition-all ${
              formData.modalitaPagamento === 'venditore'
                ? 'border-purple-500 bg-purple-50 dark:bg-purple-900/20'
                : 'border-gray-200 dark:border-gray-700 hover:border-purple-300'
            }`}
          >
            <input
              type="radio"
              name="modalitaPagamento"
              value="venditore"
              checked={formData.modalitaPagamento === 'venditore'}
              onChange={(e) => handleChange('modalitaPagamento', e.target.value as any)}
              className="mt-1 mr-3"
            />
            <div>
              <span className="font-semibold text-gray-900 dark:text-white block">Pagato dal Venditore</span>
              <span className="text-xs text-gray-600 dark:text-gray-400">Il venditore pagherà direttamente in agenzia</span>
            </div>
          </label>

          <label
            className={`flex items-start p-4 border-2 rounded-lg cursor-pointer transition-all ${
              formData.modalitaPagamento === 'bonifico'
                ? 'border-purple-500 bg-purple-50 dark:bg-purple-900/20'
                : 'border-gray-200 dark:border-gray-700 hover:border-purple-300'
            }`}
          >
            <input
              type="radio"
              name="modalitaPagamento"
              value="bonifico"
              checked={formData.modalitaPagamento === 'bonifico'}
              onChange={(e) => handleChange('modalitaPagamento', e.target.value as any)}
              className="mt-1 mr-3"
            />
            <div>
              <span className="font-semibold text-gray-900 dark:text-white block">Bonifico Istantaneo</span>
              <span className="text-xs text-gray-600 dark:text-gray-400">Pagamento anticipato effettuato dallo Studio richiedente</span>
            </div>
          </label>
        </div>
      </div>

      {/* Dati Venditore */}
      <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-6 space-y-4">
        <h3 className="text-base font-semibold text-gray-900 dark:text-white flex items-center gap-2">
          <Mail className="w-5 h-5" />
          Dati Venditore
        </h3>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Email Venditore *
          </label>
          <input
            type="email"
            value={formData.emailVenditore}
            onChange={(e) => handleChange('emailVenditore', e.target.value)}
            placeholder="venditore@example.com"
            className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
            required
          />
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
            Il venditore riceverà i dettagli dell'appuntamento e al completamento della pratica una copia dell'atto di vendita a questo indirizzo
          </p>
        </div>
      </div>

      {/* Bottoni */}
      <div className="flex justify-between pt-4 border-t border-gray-200 dark:border-gray-700">
        <button
          type="button"
          onClick={onBack}
          className="px-6 py-3 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg font-medium hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors flex items-center gap-2"
        >
          <ArrowLeft className="w-5 h-5" />
          Indietro
        </button>

        <button
          type="submit"
          className="px-6 py-3 bg-purple-600 hover:bg-purple-700 text-white rounded-lg font-medium transition-colors flex items-center gap-2"
        >
          Continua
          <ArrowRight className="w-5 h-5" />
        </button>
      </div>
    </form>
  );
};

export default StepDatiAtto;

